import {
  MEASUREMENT_ITEMS,
  TradeAreaSearchType,
  TRAVEL_MODES,
} from 'constants/tradeAreaSearch';
import { IRadioItem } from 'components/Inputs/RadioInput';
import { SuggestionIcon } from 'components/SearchInput/enums';
import { DEFAULT_INITIAL_BOUNDARY } from 'contexts/FindCompsContext';
import { IdName } from 'interfaces/IdName';
import { IProperty } from 'interfaces/IProperty';
import {
  DistanceSearch,
  GeoSpatialSearch,
  TravelTimeSearch,
} from 'interfaces/ITradeAreaSearch';
import { UnitEnum } from 'interfaces/IPropertySearch';
import { parseMapboxIntoPropertyCompany } from 'utils/parsers/parseMapboxIntoProperty';

import { CustomSearchCriteriaType } from '../EditSearchCriteria/types';

export interface InitialTravelDistanceValues {
  distanceCount: string;
  minutesCount: number;
  selectedTravelDistance: IRadioItem;
  travelMode: IdName;
  initialProperty: IProperty | null;
  unit: string;
}

export const calculateInitialTravelDistanceValues = (
  customCriteria: CustomSearchCriteriaType,
) => {
  const {
    transportation,
    lat: latTravelTime,
    lng: lngTravelTime,
    travel_time,
    propertyName: propertyNameTravel,
  } = (customCriteria?.areaSearch?.data as TravelTimeSearch) ?? {};

  const {
    latitude: latDistance,
    longitude: longDistance,
    radius,
    unit,
    propertyName: propertyNameDistance,
  } = (customCriteria?.areaSearch?.data as DistanceSearch) ?? {};

  const latitude = latDistance ?? latTravelTime;
  const longitude = longDistance ?? lngTravelTime;
  const hasProperty =
    (propertyNameTravel ?? propertyNameDistance) && latitude && longitude;

  const defaultTransportation = TRAVEL_MODES.find(
    t => t.name === transportation,
  );

  return {
    distanceCount: String(radius ?? 0),
    minutesCount: travel_time ? travel_time / 60 : 0,
    selectedTravelDistance:
      customCriteria?.areaSearch?.areaSearchType ===
      TradeAreaSearchType.TRAVEL_TIME
        ? MEASUREMENT_ITEMS[1]
        : MEASUREMENT_ITEMS[0],
    travelMode: defaultTransportation ?? TRAVEL_MODES[0],
    unit: unit ?? UnitEnum.MILES,
    initialProperty: hasProperty
      ? {
          name: propertyNameTravel ?? propertyNameDistance,
          longitude: longitude,
          latitude: latitude,
        }
      : null,
  };
};

export const calculateInitialProperty = (
  customCriteria: CustomSearchCriteriaType,
) => {
  const propertyName = (customCriteria?.areaSearch?.data as
    | DistanceSearch
    | TravelTimeSearch)?.propertyName;

  if (!propertyName) return null;
  return {
    id: 1,
    name: propertyName,
    latitude: customCriteria.latitude,
    longitude: customCriteria.longitude,
  };
};

export const calculateInitialBoundaries = (
  customCriteria: CustomSearchCriteriaType,
) => {
  if (!customCriteria.areaSearch?.areaSearchType)
    return DEFAULT_INITIAL_BOUNDARY;

  const areaSearchType = customCriteria.areaSearch.areaSearchType;

  switch (areaSearchType) {
    case TradeAreaSearchType.GEOSPATIAL: {
      const areaSearchData = customCriteria.areaSearch
        .data as GeoSpatialSearch[];

      return {
        inputSearch: '',
        selectedBoundaries: areaSearchData.map(a => {
          return {
            countryCode: a.countryCode,
            geohierarchyLevel: a.geohierarchyLevel,
            geoIds: a.geoIds,
            name: a?.geoNames?.[0]?.id,
            type: SuggestionIcon.globalBoundary,
          };
        }),
      };
    }
    default: {
      const areaSearchData = customCriteria.areaSearch.data as DistanceSearch;
      const propertyName = areaSearchData?.propertyName;

      const getPropertyNameString = (separator = '') => {
        if (Array.isArray(propertyName)) {
          return propertyName.map(p => p.id).join(separator);
        }
        return propertyName || '';
      };

      const inputSearch = getPropertyNameString(',');
      const displayAddress = getPropertyNameString('');

      return {
        inputSearch,
        selectedBoundaries: parseMapboxIntoPropertyCompany([
          {
            mapBoxDetail: {
              displayAddress: displayAddress,
              longitude: areaSearchData?.longitude,
              latitude: areaSearchData?.latitude,
            },
          },
        ]),
      };
    }
  }
};
